import type { Command } from "../document/commands.ts";
import type { WorldDocument } from "../document/types.ts";
import { commandToOps } from "./command-ops.ts";
import type { LiveOp } from "./op-types.ts";
import { liveRepaintOf } from "./repaint.ts";
import { afterTilesets, capped } from "./session-history.ts";
import type { EditEntry, LiveSession, UndoStep } from "./session-types.ts";
import { opsRefusal, queued, sessionIds, stepOf } from "./session.ts";
import { inverseAgainst, type Inverse } from "./undo-inverse.ts";

/**
 * Per-user undo and redo (ADR-086 decision 27). The tab's undo list holds only its own edits. Undoing one
 * builds its inverse against the shown document, key by key (`undo-inverse.ts`), and sends it as a new
 * operation like any other edit, so everyone sees the undo and nobody else's work is taken back. The commands
 * that operation resolves to become the entry to redo, and redo is the same rule in the other direction: it
 * puts back only what still holds what the undo left.
 */

/**
 * The operations that carry an inverse, built from the shown document under the session's next ids.
 *
 * @param session The session.
 * @param commands The inverse's commands.
 * @returns The operations of one edit group, none when the commands write nothing, or null when a part cannot
 *   travel.
 */
function opsFor(session: LiveSession, commands: readonly Command[]): readonly LiveOp[] | null {
  return commands.length === 0 ? [] : commandToOps({ kind: "compound", commands }, session.shown, sessionIds(session));
}

/**
 * The inverse of an entry against the shown document, with the operations that carry it. When a part of the
 * inverse cannot travel as an operation (a name longer than an operation carries, an item larger than a
 * frame), the inverse is built again without the parts that cannot, each counted as skipped.
 *
 * @param session The session.
 * @param entry The edit to take back.
 * @returns The inverse and its operations.
 */
function inverseOf(session: LiveSession, entry: EditEntry): { readonly inverse: Inverse; readonly ops: readonly LiveOp[] } {
  const commands = entry.parts.flatMap((part) => part.commands);
  const whole = inverseAgainst(session.shown, commands);
  const ops = opsFor(session, whole.commands);
  if (ops !== null) {
    return { inverse: whole, ops };
  }
  /**
   * Whether one command can be sent from a document.
   *
   * @param command The command.
   * @param document The document it applies to.
   * @returns True when `commandToOps` gives operations for it.
   */
  const travels = (command: Command, document: WorldDocument): boolean => commandToOps(command, document, sessionIds(session)) !== null;
  const partial = inverseAgainst(session.shown, commands, travels);
  return { inverse: partial, ops: opsFor(session, partial.commands) ?? [] };
}

/**
 * An undo or a redo that queued nothing.
 *
 * @param session The session after it.
 * @param status What became of it.
 * @param skipped How many keys were left.
 * @param passed How many entries were passed over.
 * @returns The step.
 */
function idle(session: LiveSession, status: UndoStep["status"], skipped: number, passed: number): UndoStep {
  return { ...stepOf(session), status, kept: 0, skipped, passed };
}

/**
 * Takes back the newest entry of one list that can be taken back, and records what that did on the other.
 * An entry whose targets are gone for now (a layer someone deleted) is passed over and kept, so it neither
 * blocks the edits before it nor is lost for when its target returns; an entry nothing is left of because
 * someone changed every key it touched is dropped.
 *
 * @param session The session; never written into.
 * @param now The time.
 * @param redo Whether the entry comes from the redo list.
 * @returns The step.
 */
function turned(session: LiveSession, now: number, redo: boolean): UndoStep {
  const entries = redo ? session.future : session.past;
  const refused = opsRefusal(session, [], now);
  if (refused !== null) {
    return { ...stepOf(session, { refused }), status: null, kept: 0, skipped: 0, passed: 0 };
  }
  let waiting: number | null = null;
  let passed = 0;
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const { inverse, ops } = inverseOf(session, entries[index] as EditEntry);
    const rest = entries.filter((_, i) => i !== index);
    const first = ops[0];
    if (first === undefined && inverse.gone > 0) {
      waiting = waiting ?? inverse.skipped;
      passed += 1;
      continue;
    }
    if (first === undefined) {
      return idle(redo ? { ...session, future: rest } : { ...session, past: rest }, "nothing", inverse.skipped + inverse.kept, passed);
    }
    const full = opsRefusal(session, ops, now);
    if (full !== null) {
      return { ...stepOf(session, { refused: full }), status: null, kept: 0, skipped: 0, passed: 0 };
    }
    const taken = queued(session, ops);
    const done: EditEntry = { group: first.group, parts: taken.parts };
    const lost = taken.outcomes.reduce((sum, outcomes) => sum + outcomes.filter((outcome) => outcome.status === "skipped").length, 0);
    const history = redo ? { past: capped([...session.past, done]), future: rest } : { past: rest, future: [...session.future, done] };
    const next = afterTilesets(session.shown, { ...taken.session, ...history });
    const skipped = inverse.skipped + lost;
    return { ...stepOf(next.session, { outcomes: taken.outcomes, repaint: liveRepaintOf(session.shown, next.session.shown), historyDropped: next.dropped }), status: skipped > 0 ? "partial" : "done", kept: inverse.kept, skipped, passed };
  }
  return waiting === null ? idle(session, "empty", 0, 0) : idle(session, "gone", waiting, passed);
}

/**
 * Undoes the tab's newest edit as far as it still can be.
 *
 * @param session The session; never written into.
 * @param now The time.
 * @returns The step. `done` or `partial`: the inverse is pending as operations of one edit group, shown at
 *   once, and the edit waits on the redo list as what the undo did. `nothing`: someone changed everything the
 *   edit touched, nothing is sent and the entry is dropped; the rest of the list stays. `gone`: what every
 *   edit on the list touched is gone for now, nothing is sent and every entry is kept. `empty`: the tab has no
 *   edit to undo. A session that takes no command refuses with its reason and keeps the entry.
 */
export function undoEdit(session: LiveSession, now: number): UndoStep {
  return turned(session, now, false);
}

/**
 * Redoes the edit the tab undid last, by the same rule in the other direction: what the undo put back goes
 * forward again only where it still holds what the undo left.
 *
 * @param session The session; never written into.
 * @param now The time.
 * @returns The step, as `undoEdit`'s, the redone edit going back on the undo list.
 */
export function redoEdit(session: LiveSession, now: number): UndoStep {
  return turned(session, now, true);
}
